import type { HostSpecsResponse } from '@/tauri/controller/system';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Session cache for the desktop host machine's capability facts.
 * Field contract: `docs/desktop/host-machine-specs-context-contract.md`.
 *
 * Collection stays off the boot path: never block app or analytics boot for
 * this. The worst acceptable case is the fields never being set.
 */

let cachedHostSpecs: HostSpecsResponse | undefined;
let inFlightLoad: Promise<void> | undefined;
const MAX_LOAD_ATTEMPTS = 2;

export const getHostSpecs = (): HostSpecsResponse | undefined => {
  if (!isTauri()) return undefined;
  return cachedHostSpecs;
};

export const initHostSpecs = async (): Promise<void> => {
  if (!isTauri()) return;
  if (cachedHostSpecs) return;

  if (!inFlightLoad) {
    inFlightLoad = (async () => {
      // Lazy import: keeps the IPC module graph out of tests that only touch
      // the sync getter.
      const { systemController } = await import('@/tauri/controller/system');
      for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt += 1) {
        try {
          const specs = await systemController.get_host_specs();
          // Rust omits every field it could not answer, so an empty payload
          // means "this machine told us nothing". Leave the cache unset so a
          // later lifecycle call can retry, rather than caching a blank.
          if (!specs || Object.keys(specs).length === 0) return;
          cachedHostSpecs = specs;
          return;
        } catch (err) {
          logger.error(
            `[hostSpecs] get_host_specs() failed (attempt ${attempt}/${MAX_LOAD_ATTEMPTS}):`,
            err
          );
        }
      }
    })().finally(() => {
      if (!cachedHostSpecs) {
        // Allow a later lifecycle call to start another bounded attempt sequence.
        inFlightLoad = undefined;
      }
    });
  }
  await inFlightLoad;
};
